import React, { useState, useEffect } from 'react'
import { X, User, Lock, Save, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../services/api'

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  user: 'User',
}

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800',
  admin: 'bg-red-100 text-red-800',
  manager: 'bg-blue-100 text-blue-800',
  user: 'bg-green-100 text-green-800',
}

const UserProfileModal = ({ onClose }) => {
  const { user, updateUser } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')

  // Profile form
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
  })
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileMsg, setProfileMsg] = useState(null) // { type: 'success'|'error', text }

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState(null)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [forgotMode, setForgotMode] = useState(false) // skip current password

  // Pre-fill profile fields from current user
  // API returns camelCase: firstName, lastName
  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.firstName || user.first_name || '',
        last_name: user.lastName || user.last_name || '',
        username: user.username || '',
        email: user.email || '',
      })
    }
  }, [user])

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleProfileSave = async () => {
    setProfileMsg(null)
    if (!profileForm.username.trim()) {
      setProfileMsg({ type: 'error', text: 'Username is required' })
      return
    }
    setProfileLoading(true)
    try {
      await api.updateUser(user.id, {
        first_name: profileForm.first_name.trim(),
        last_name: profileForm.last_name.trim(),
        username: profileForm.username.trim(),
        email: profileForm.email.trim(),
      })
      // Merge back as camelCase to match what AuthContext stores
      updateUser({
        ...user,
        firstName: profileForm.first_name.trim(),
        lastName: profileForm.last_name.trim(),
        username: profileForm.username.trim(),
        email: profileForm.email.trim(),
      })
      setProfileMsg({ type: 'success', text: 'Profile updated successfully' })
    } catch (err) {
      setProfileMsg({ type: 'error', text: err.message || 'Failed to update profile' })
    } finally {
      setProfileLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    setPasswordMsg(null)

    if (forgotMode) {
      // Set new password without current password (session-authenticated)
      if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
        setPasswordMsg({ type: 'error', text: 'Please fill in both password fields' })
        return
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setPasswordMsg({ type: 'error', text: 'New passwords do not match' })
        return
      }
      if (passwordForm.newPassword.length < 6) {
        setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters' })
        return
      }
      setPasswordLoading(true)
      try {
        await api.setPassword(passwordForm.newPassword)
        setPasswordMsg({ type: 'success', text: 'Password updated successfully' })
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
        setForgotMode(false)
      } catch (err) {
        setPasswordMsg({ type: 'error', text: err.message || 'Failed to update password' })
      } finally {
        setPasswordLoading(false)
      }
      return
    }

    // Normal mode — requires current password
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'All password fields are required' })
      return
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'New passwords do not match' })
      return
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'New password must be at least 6 characters' })
      return
    }
    setPasswordLoading(true)
    try {
      await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword)
      setPasswordMsg({ type: 'success', text: 'Password changed successfully' })
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err.message || 'Failed to change password' })
    } finally {
      setPasswordLoading(false)
    }
  }

  const initials = (user?.firstName?.charAt(0) || user?.first_name?.charAt(0) || user?.username?.charAt(0) || '?').toUpperCase()

  const displayName = (() => {
    const first = user?.firstName || user?.first_name || ''
    const last = user?.lastName || user?.last_name || ''
    return (first + ' ' + last).trim() || user?.username
  })()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-6 pt-6 pb-4">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {initials}
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg leading-tight">
                {displayName}
              </h2>
              <p className="text-gray-400 text-sm">{user?.email || 'No email set'}</p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[user?.role] || 'bg-gray-100 text-gray-800'}`}>
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            <button
              onClick={() => { setActiveTab('profile'); setProfileMsg(null) }}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'bg-white text-gray-800'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <User size={14} />
              Edit Profile
            </button>
            <button
              onClick={() => { setActiveTab('password'); setPasswordMsg(null) }}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'password'
                  ? 'bg-white text-gray-800'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Lock size={14} />
              Change Password
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">

          {/* ── Profile Tab ── */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name</label>
                  <input
                    type="text"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="First name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={profileForm.username}
                  onChange={(e) => setProfileForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="Username"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {profileMsg && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  profileMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {profileMsg.type === 'success'
                    ? <CheckCircle size={15} />
                    : <AlertCircle size={15} />}
                  {profileMsg.text}
                </div>
              )}

              <button
                onClick={handleProfileSave}
                disabled={profileLoading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                {profileLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save size={15} />
                )}
                {profileLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {/* ── Password Tab ── */}
          {activeTab === 'password' && (
            <div className="space-y-4">

{/* Current Password — hidden in forgot mode */}
              {!forgotMode && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                      placeholder="Enter current password"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              )}

              {/* New Password */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {/* Match indicator */}
                {passwordForm.confirmPassword && (
                  <p className={`text-xs mt-1 ${
                    passwordForm.newPassword === passwordForm.confirmPassword
                      ? 'text-green-600'
                      : 'text-red-500'
                  }`}>
                    {passwordForm.newPassword === passwordForm.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              {passwordMsg && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  passwordMsg.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {passwordMsg.type === 'success'
                    ? <CheckCircle size={15} />
                    : <AlertCircle size={15} />}
                  {passwordMsg.text}
                </div>
              )}

              <button
                onClick={handlePasswordChange}
                disabled={passwordLoading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                {passwordLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Lock size={15} />
                )}
                {passwordLoading ? 'Saving...' : forgotMode ? 'Set New Password' : 'Change Password'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserProfileModal
